import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useMonth } from "../context/MonthContext";
import { 
  CaretLeft, Calculator, ArrowsClockwise, FloppyDisk,
  Check, Warning, CurrencyDollar, Export
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = {
  get: (url) => axios.get(`${API}${url}`),
  post: (url, data) => axios.post(`${API}${url}`, data),
  put: (url, data) => axios.put(`${API}${url}`, data),
};

const formatCurrency = (amount, currency = "INR") => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const StatusBadge = ({ status }) => {
  const statusClasses = {
    active: "badge-active",
    inactive: "badge-inactive",
    draft: "badge-draft",
    final: "badge-final",
    revised: "badge-revised",
  };
  
  return (
    <span className={`badge ${statusClasses[status] || "badge-draft"}`}>
      {status}
    </span>
  );
};

export const BillingCalculator = () => {
  const { clientId, month: paramMonth } = useParams();
  const { selectedMonth } = useMonth();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Priority: URL param > global month context
  const [month, setMonth] = useState(() => {
    return paramMonth || selectedMonth;
  });
  
  const [entry, setEntry] = useState({
    revenue: 0,
    ad_spend: 0,
    roi: 0,
    fx_rate: 1.0,
    adjustments: 0,
    adjustment_notes: "",
  });
  
  const [calculation, setCalculation] = useState(null);
  const [shopifyData, setShopifyData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientRes = await api.get(`/clients/${clientId}`);
        setClient(clientRes.data.client);
        
        // Try to load existing calculation
        try {
          const calcRes = await api.get(`/billing/${clientId}/${month}`);
          if (calcRes.data.calculation) {
            setCalculation(calcRes.data.calculation);
            setEntry({
              revenue: calcRes.data.calculation.revenue || 0,
              ad_spend: calcRes.data.calculation.ad_spend || 0,
              roi: calcRes.data.calculation.roi || 0,
              fx_rate: calcRes.data.calculation.fx_rate || 1.0,
              adjustments: calcRes.data.calculation.adjustments || 0,
              adjustment_notes: calcRes.data.calculation.adjustment_notes || "",
            });
          }
        } catch (e) {
          // No existing calculation
        }
        
        // Try to load Shopify revenue
        try {
          const revenueRes = await api.get(`/revenue/${clientId}/${month}`);
          if (revenueRes.data.raw) {
            setShopifyData(revenueRes.data.raw);
            // Auto-fill revenue if not already set
            if (!calculation && revenueRes.data.raw.net_sales) {
              setEntry(prev => ({ ...prev, revenue: revenueRes.data.raw.net_sales }));
            }
          }
        } catch (e) {
          // No Shopify data
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId, month]);

  const syncShopify = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/revenue/${clientId}/${month}/sync`);
      setShopifyData(res.data.data);
      setEntry(prev => ({ ...prev, revenue: res.data.data.net_sales || 0 }));
      alert("Shopify data synced successfully!");
    } catch (err) {
      console.error("Failed to sync:", err);
      alert("Failed to sync Shopify data. Make sure a Shopify store is linked.");
    } finally {
      setSyncing(false);
    }
  };

  const calculateBilling = async () => {
    setCalculating(true);
    try {
      const res = await api.post(`/billing/${clientId}/${month}/calculate`, {
        client_id: clientId,
        month: month,
        ...entry,
      });
      setCalculation(res.data.calculation);
      alert("Billing calculated successfully!");
    } catch (err) {
      console.error("Failed to calculate:", err);
      alert("Failed to calculate billing");
    } finally {
      setCalculating(false);
    }
  };

  const finalizeBilling = async () => {
    if (!window.confirm("Are you sure you want to finalize this billing? This will lock the calculation.")) {
      return;
    }
    try {
      await api.put(`/billing/${clientId}/${month}/finalize`);
      setCalculation(prev => ({ ...prev, status: "final" }));
      alert("Billing finalized!");
    } catch (err) {
      console.error("Failed to finalize:", err);
      alert("Failed to finalize billing");
    }
  };

  const createInvoice = async (type) => {
    try {
      const res = await api.post(`/invoices/${clientId}/${month}/create?invoice_type=${type}`);
      if (res.data.success) {
        alert(`${type.charAt(0).toUpperCase() + type.slice(1)} invoice created! Invoice #: ${res.data.invoice.zoho_invoice_number}`);
      } else {
        alert("Failed to create invoice: " + (res.data.error || "Unknown error"));
      }
    } catch (err) {
      console.error("Failed to create invoice:", err);
      alert("Failed to create invoice");
    }
  };

  // Calculate ROI when revenue or ad_spend changes
  const updateROI = () => {
    if (entry.ad_spend > 0) {
      const roi = entry.revenue / entry.ad_spend;
      setEntry(prev => ({ ...prev, roi: parseFloat(roi.toFixed(2)) }));
    }
  };

  if (loading) {
    return <div className="p-8 text-[#A1A1AA]">Loading...</div>;
  }

  return (
    <div data-testid="billing-calculator-page">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/billing" className="btn-ghost">
          <CaretLeft size={20} />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Inter' }}>
            Monthly Billing
          </h1>
          <p className="text-[#52525B] mt-1">{client?.client_name} - {client?.project_name}</p>
        </div>
        <div className="form-group mb-0">
          <input
            type="month"
            className="input-default"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Shopify Revenue Section */}
          {client?.shopify_store && (
            <div className="card-flat p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ fontFamily: 'Inter' }}>
                  Shopify Revenue
                </h3>
                <button 
                  onClick={syncShopify} 
                  className="btn-secondary flex items-center gap-2"
                  disabled={syncing}
                >
                  <ArrowsClockwise size={18} className={syncing ? "animate-spin" : ""} />
                  {syncing ? "Syncing..." : "Sync from Shopify"}
                </button>
              </div>
              
              {shopifyData ? (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="small-label">Gross Sales</span>
                    <p className="data-number text-lg">{formatCurrency(shopifyData.gross_sales, shopifyData.currency)}</p>
                  </div>
                  <div>
                    <span className="small-label">Discounts</span>
                    <p className="data-number text-lg text-[#FF2A2A]">-{formatCurrency(shopifyData.discounts, shopifyData.currency)}</p>
                  </div>
                  <div>
                    <span className="small-label">Refunds</span>
                    <p className="data-number text-lg text-[#FF2A2A]">-{formatCurrency(shopifyData.refunds, shopifyData.currency)}</p>
                  </div>
                  <div>
                    <span className="small-label">Net Sales</span>
                    <p className="data-number text-lg font-semibold text-[#00C853]">{formatCurrency(shopifyData.net_sales, shopifyData.currency)}</p>
                  </div>
                  <div>
                    <span className="small-label">Order Count</span>
                    <p className="data-number text-lg">{shopifyData.order_count}</p>
                  </div>
                </div>
              ) : (
                <p className="text-[#A1A1AA]">No data synced yet. Click "Sync from Shopify" to fetch revenue data.</p>
              )}
            </div>
          )}

          {/* Billing Inputs */}
          <div className="card-flat p-6">
            <h3 className="font-semibold mb-4" style={{ fontFamily: 'Inter' }}>
              Billing Inputs
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Revenue ({client?.currency})</label>
                <input
                  type="number"
                  className="input-default w-full"
                  value={entry.revenue}
                  onChange={(e) => setEntry({ ...entry, revenue: parseFloat(e.target.value) || 0 })}
                  onBlur={updateROI}
                  data-testid="input-revenue"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Ad Spend ({client?.currency})</label>
                <input
                  type="number"
                  className="input-default w-full"
                  value={entry.ad_spend}
                  onChange={(e) => setEntry({ ...entry, ad_spend: parseFloat(e.target.value) || 0 })}
                  onBlur={updateROI}
                  data-testid="input-ad-spend"
                />
              </div>
              <div className="form-group">
                <label className="form-label">ROI (Revenue / Ad Spend)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-default w-full"
                  value={entry.roi}
                  onChange={(e) => setEntry({ ...entry, roi: parseFloat(e.target.value) || 0 })}
                  data-testid="input-roi"
                />
              </div>
              <div className="form-group">
                <label className="form-label">FX Rate (to INR)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-default w-full"
                  value={entry.fx_rate}
                  onChange={(e) => setEntry({ ...entry, fx_rate: parseFloat(e.target.value) || 1 })}
                  data-testid="input-fx-rate"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Adjustments ({client?.currency})</label>
                <input
                  type="number"
                  className="input-default w-full"
                  value={entry.adjustments}
                  onChange={(e) => setEntry({ ...entry, adjustments: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Adjustment Notes</label>
                <input
                  type="text"
                  className="input-default w-full"
                  value={entry.adjustment_notes}
                  onChange={(e) => setEntry({ ...entry, adjustment_notes: e.target.value })}
                />
              </div>
            </div>

            <button 
              onClick={calculateBilling} 
              className="btn-primary flex items-center gap-2 mt-6"
              disabled={calculating}
              data-testid="calculate-btn"
            >
              <Calculator size={18} />
              {calculating ? "Calculating..." : "Calculate Billing"}
            </button>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          <div className="card-flat p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold" style={{ fontFamily: 'Inter' }}>
                Calculation Results
              </h3>
              {calculation && <StatusBadge status={calculation.status} />}
            </div>
            
            {calculation ? (
              <div className="space-y-4">
                <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <span className="small-label">Fixed Fee</span>
                  <p className="data-number text-2xl">{formatCurrency(calculation.fixed_fee_billable, client?.currency)}</p>
                </div>
                <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <span className="small-label">Variable Fee</span>
                  <p className="data-number text-2xl">{formatCurrency(calculation.variable_fee_billable, client?.currency)}</p>
                </div>
                <div className="p-4 bg-[#1A1A00] border border-[#FFEB00]/30">
                  <span className="small-label text-[#FFEB00]">Total Billable</span>
                  <p className="data-number text-3xl" style={{color: '#FFEB00'}}>{formatCurrency(calculation.total_billable_fc, client?.currency)}</p>
                  {client?.currency !== "INR" && (
                    <p className="text-sm text-[#A1A1AA] mt-1">
                      {formatCurrency(calculation.total_billable_inr, "INR")}
                    </p>
                  )}
                </div>
                <div className="p-4 bg-[#001A0D] border border-[#00C853]/30">
                  <span className="small-label" style={{color: '#00C853'}}>Commission Payable</span>
                  <p className="data-number text-xl" style={{color: '#00C853'}}>{formatCurrency(calculation.commission_payable, client?.currency)}</p>
                </div>

                <div className="pt-4 border-t border-[#2A2A2A] space-y-2">
                  <p className="text-xs text-[#A1A1AA]">Version: {calculation.version}</p>
                  {calculation.status !== "final" && (
                    <button onClick={finalizeBilling} className="btn-secondary w-full flex items-center justify-center gap-2">
                      <Check size={18} />
                      Finalize
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-[#A1A1AA]">
                <Calculator size={48} className="mx-auto mb-4 opacity-50" />
                <p>Enter billing inputs and click "Calculate" to see results</p>
              </div>
            )}
          </div>

          {/* Invoice Actions */}
          {calculation && calculation.status === "final" && (
            <div className="card-flat p-6">
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Inter' }}>
                Create Invoice
              </h3>
              <div className="space-y-2">
                {calculation.fixed_fee_billable > 0 && (
                  <button 
                    onClick={() => createInvoice("fixed")} 
                    className="btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    <Export size={18} />
                    Create Fixed Fee Invoice
                  </button>
                )}
                {calculation.variable_fee_billable > 0 && (
                  <button 
                    onClick={() => createInvoice("variable")} 
                    className="btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    <Export size={18} />
                    Create Variable Fee Invoice
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingCalculator;
